import React, { useState, useRef, useEffect } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

import { categoryStatuses } from '../../util/categoryConsts'
import { theme } from '../../themes/theme'
import { WhiteBGContainer, FlexRow } from '../styles/Containers'
import { SecondaryButton } from '../common/Button'
import CategoryCard from './CategoryCard'

const SelectorContainer = styled(FlexRow)`
  flex: 1;
  min-width: 0;
`

const NavButton = styled(SecondaryButton)`
  padding: 7px;
  min-width: 0;

  .MuiSvgIcon-root {
    width: 25px;
    height: 25px;
    transform: scale(1.2);
    transform-origin: center;
  }
`

const CardsContainer = styled(FlexRow)`
  width: 0;
  flex: 1;
  flex-wrap: nowrap;
  overflow-y: hidden;
  overflow-x: scroll;
  scroll-behavior: smooth;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  /* Hide scrollbar for Chrome, Safari, and Opera */
  .scrollable-content::-webkit-scrollbar {
    display: none;
  }
`

const Card = styled(WhiteBGContainer)`
  padding: 20px;
  background-color: ${props => (props.active ? theme.colors.primaryExtraLight : theme.colors.background.white)};
  cursor: pointer;
  min-width: 132px;
  max-width: 276px;
  width: 100%;
`

function CategorySelector({ defaultCategory, categories = [], onSelectCategory = () => {} }) {
  const [showButtons, setShowButtons] = useState()
  const [activeCategory, setActiveCategory] = useState()
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(false)

  const scrollRef = useRef(null)

  function handleScroll() {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth, scrollWidth } = scrollRef.current

      const start = scrollLeft < 5
      const end = scrollLeft + clientWidth >= scrollWidth - 5

      setIsAtStart(start)
      setIsAtEnd(end)

      if (start && end) setShowButtons(false)
      else setShowButtons(true)
    }
  }

  function scrollTo(direction) {
    if (scrollRef.current) {
      const currentScrollLeft = scrollRef.current.scrollLeft
      const scrollAmount = direction === 'left' ? currentScrollLeft - 200 : currentScrollLeft + 200

      scrollRef.current.scrollTo({
        left: scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  useEffect(() => {
    const currentRef = scrollRef.current
    window.addEventListener('resize', handleScroll)
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll)
      handleScroll()
    }
    return () => {
      window.removeEventListener('resize', handleScroll)
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    if (defaultCategory) setActiveCategory(defaultCategory)
  }, [defaultCategory])

  function selectCategory(categoryId) {
    setActiveCategory(categoryId)
    onSelectCategory(categoryId)
  }

  return (
    <SelectorContainer>
      {showButtons && (
        <NavButton onClick={() => scrollTo('left')} disabled={isAtStart} aria-label="Scroll left">
          <ArrowBackIosIcon viewBox="0 0 15 24" />
        </NavButton>
      )}
      <CardsContainer ref={scrollRef}>
        {categories.map(c => {
          return (
            <Card gap="10px" padding="20px" active={activeCategory === c.id} onClick={() => selectCategory(c.id)} key={c.id}>
              <CategoryCard name={c.name} status={c.status} numberOfFiles={c.numberOfFiles} size={c.size} />
            </Card>
          )
        })}
      </CardsContainer>
      {showButtons && (
        <NavButton onClick={() => scrollTo('right')} disabled={isAtEnd} aria-label="Scroll right">
          <ArrowForwardIosIcon />
        </NavButton>
      )}
    </SelectorContainer>
  )
}

CategorySelector.propTypes = {
  defaultCategory: PropTypes.string,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.oneOf(categoryStatuses),
      numberOfFiles: PropTypes.number.isRequired,
      size: PropTypes.number.isRequired
    })
  ),
  onSelectCategory: PropTypes.func
}

export default CategorySelector
